"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { TrendingUp, TrendingDown, MapPin, DollarSign, Clock, House, ArrowRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const markets = [{
  id: "crime-statistics",
  name: "Crime Statistics",
  description: "Comprehensive breakdown of incident reports and resolution rates across the city.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_crime_stats.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_crime_stats.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_crime_stats.png-wvc-sizes",
  imageAlt: "Digital dashboard showing crime trends",
  medianPrice: "94% Clear",
  daysOnMarket: 12,
  activeListings: 1240,
  trend: "down",
  trendValue: "-5.2%"
}, {
  id: "budget-allocation",
  name: "Budget Allocation",
  description: "Transparent view of how public funds are utilized for community safety and equipment.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_budget_transparency.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_budget_transparency.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_budget_transparency.png-wvc-sizes",
  imageAlt: "Financial report documents",
  medianPrice: "$42.5M",
  daysOnMarket: 365,
  activeListings: 12,
  trend: "up",
  trendValue: "+2.8%"
}, {
  id: "officer-demographics",
  name: "Officer Demographics",
  description: "Detailed insights into the diversity and composition of our dedicated police force.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_force_demographics.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_force_demographics.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_force_demographics.png-wvc-sizes",
  imageAlt: "Police officers in community meeting",
  medianPrice: "850 Staff",
  daysOnMarket: 8,
  activeListings: 45,
  trend: "up",
  trendValue: "+12.5%"
}, {
  id: "response-times",
  name: "Response Times",
  description: "Average dispatch and arrival times for emergency calls within metropolitan limits.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_response_metrics.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_response_metrics.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_response_metrics.png-wvc-sizes",
  imageAlt: "Police cruiser with lights active",
  medianPrice: "4.2 Mins",
  daysOnMarket: 24,
  activeListings: 110,
  trend: "down",
  trendValue: "-1.1%"
}, {
  id: "community-engagement",
  name: "Community Outreach",
  description: "Metrics on public programs, neighborhood watch meetings, and youth initiatives.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_community_outreach.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_community_outreach.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_community_outreach.png-wvc-sizes",
  imageAlt: "Officer talking with local residents",
  medianPrice: "320 Events",
  daysOnMarket: 30,
  activeListings: 88,
  trend: "up",
  trendValue: "+15.4%"
}];
export default function AccountabilityDashboard() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="254111" id="accountability-dashboard" className="w-full py-16 md:py-24 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <div ref={headerRef} className={`flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4 bg-primary/10 text-primary border-primary/20 font-mono">
            OFFICIAL DATA HUB
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight mb-4 text-primary">
            Accountability Dashboard
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Providing the citizens of Metropolitan Central with transparent, real-time insights into our operations and community impact.
          </p>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 transition-all duration-1000 ease-out delay-200 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {markets.map((market, i) => <Card key={market.id} data-index={i} className="overflow-hidden flex flex-col h-full shadow-md hover:shadow-lg transition-shadow duration-300 border-border bg-card">
              <div className="relative">
                <AspectRatio ratio={16 / 9}>
                  <img src={market.imageSrc} data-wvc-srcset={market.imageSrcSet} data-wvc-sizes={market.imageSizes} alt={market.imageAlt} className="object-cover w-full h-full transition-transform duration-500 hover:scale-105 grayscale-[0.2] hover:grayscale-0" loading="lazy" />
                </AspectRatio>
                <div className="absolute top-3 right-3">
                  <Badge variant={market.trend === "up" ? "default" : "secondary"} className="shadow-sm backdrop-blur-md bg-background/95 text-foreground border-border font-mono">
                    {market.trend === "up" ? <TrendingUp className="w-3 h-3 mr-1 text-secondary" /> : <TrendingDown className="w-3 h-3 mr-1 text-destructive" />}
                    {market.trendValue}
                  </Badge>
                </div>
              </div>

              <CardHeader className="pb-4">
                <div className="flex items-center justify-between mb-1">
                  <CardTitle className="text-xl font-default font-bold text-primary">{market.name}</CardTitle>
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                </div>
                <CardDescription className="line-clamp-2 font-default text-muted-foreground">
                  {market.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="pb-6 flex-grow">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col space-y-1">
                    <span className="text-xs text-muted-foreground flex items-center font-mono uppercase tracking-wider">
                      <DollarSign className="w-3 h-3 mr-1" /> Key Metric
                    </span>
                    <span className="font-bold text-foreground font-default">{market.medianPrice}</span>
                  </div>
                  <div className="flex flex-col space-y-1">
                    <span className="text-xs text-muted-foreground flex items-center font-mono uppercase tracking-wider">
                      <Clock className="w-3 h-3 mr-1" /> Reporting Cycle
                    </span>
                    <span className="font-bold text-foreground font-default">{market.daysOnMarket} Days</span>
                  </div>
                  <div className="col-span-2 pt-2">
                    <Separator className="mb-3 bg-border" />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground flex items-center font-mono uppercase tracking-wider">
                        <House className="w-3 h-3 mr-1" /> Total Records
                      </span>
                      <span className="font-medium text-sm font-default">{market.activeListings} entries</span>
                    </div>
                  </div>
                </div>
              </CardContent>

              <CardFooter className="pt-0 mt-auto">
                <Button asChild variant="outline" className="w-full group border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md">
                  <Link to="/TransparencyPage">
                    View Detailed Report
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>

        {}
        <div className="mt-16 text-center">
          <p className="text-muted-foreground mb-6 font-default">
            Need specific data for your neighborhood or a Freedom of Information request?
          </p>
          <Button asChild size="lg" className="rounded-md px-8 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
            <Link to="/TransparencyPage">
              Request Official Records
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}